import React from "react";
import { Mail, Phone } from "lucide-react";
import { ButtonLink } from "../../components/ui/button";
import { LinkAnchor } from "../../components/ui/link";
import type { ContractorSeoBusinessData } from "./types";

type ContractorSeoCtaProps = {
  business: ContractorSeoBusinessData;
  heading?: string;
};

export const ContractorSeoCta: React.FC<ContractorSeoCtaProps> = ({
  business,
  heading = "Ready to get started?",
}) => {
  const phone = business.phone?.trim();
  const email = business.businessEmail?.trim();
  const hasContactFallback = Boolean(phone || email);
  const showQuoteForm = business.quoteFormEnabled !== false;

  return (
    <section className="mt-12 rounded-lg border border-border bg-card p-6 text-center">
      <p className="font-display text-xl font-semibold text-foreground">
        {heading}
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        {showQuoteForm
          ? "Send us your project details through our online quote form for the fastest response."
          : `Get in touch with ${business.businessName} to discuss your project.`}
      </p>

      <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
        {showQuoteForm ? (
          <ButtonLink href="/#contact" size="lg">
            Get an Online Quote
          </ButtonLink>
        ) : phone ? (
          <ButtonLink href={`tel:${phone}`} size="lg">
            Call {phone}
          </ButtonLink>
        ) : email ? (
          <ButtonLink href={`mailto:${email}`} size="lg">
            Email Us
          </ButtonLink>
        ) : null}
      </div>

      {showQuoteForm && hasContactFallback ? (
        <p className="mt-5 text-xs text-muted-foreground">
          Prefer to talk?{" "}
          {phone ? (
            <>
              Call{" "}
              <LinkAnchor
                href={`tel:${phone}`}
                className="inline-flex items-center gap-1 font-medium text-foreground hover:text-accent"
              >
                <Phone className="h-3 w-3" />
                {phone}
              </LinkAnchor>
            </>
          ) : null}
          {phone && email ? <span> or </span> : null}
          {email ? (
            <>
              email{" "}
              <LinkAnchor
                href={`mailto:${email}`}
                className="inline-flex items-center gap-1 font-medium text-foreground hover:text-accent"
              >
                <Mail className="h-3 w-3" />
                {email}
              </LinkAnchor>
            </>
          ) : null}
          .
        </p>
      ) : null}
    </section>
  );
};
